import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  StudentPortalLanguageProvider,
  useStudentPortalLanguage
} from './contexts/StudentPortalLanguageContext';
import { StudentPortalLoginPage } from './StudentPortalLoginPage';
import { StudentAcademySelectModal } from './StudentAcademySelectModal';
import { StudentPortalDashboard } from './StudentPortalDashboard';
import { academyApi } from '../../services/academyApi';
import {
  Student, PortalAcademy, AcademySession, AcademyAttendance
} from '../../types/academy';

const formatLogoUrl = (raw: string | undefined | null): string => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `data:image/png;base64,${trimmed}`;
};

const formatPhotoUrl = (raw: string | undefined | null): string => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed.startsWith('data:') || trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `data:image/jpeg;base64,${trimmed}`;
};

const StudentPortalInner: React.FC = () => {
  const { t } = useStudentPortalLanguage();

  const [token, setToken] = useState<string | null>(() => {
    const t = localStorage.getItem('blobtic_portal_student_token');
    return t && t !== 'undefined' && t !== 'null' ? t : null;
  });
  const [lastLoginIdent, setLastLoginIdent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(() => {
    const t = localStorage.getItem('blobtic_portal_student_token');
    return !!(t && t !== 'undefined' && t !== 'null');
  });
  const [error, setError] = useState<string | null>(null);

  // Portal State
  const [student, setStudent] = useState<Student | null>(null);
  const [childrenList, setChildrenList] = useState<any[]>([]);
  const [academies, setAcademies] = useState<PortalAcademy[]>([]);
  const [selectedAcademy, setSelectedAcademy] = useState<PortalAcademy | null>(null);
  const [showAcademyModal, setShowAcademyModal] = useState<boolean>(false);

  // Data
  const [sessions, setSessions] = useState<AcademySession[]>([]);
  const [attendances, setAttendances] = useState<AcademyAttendance[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [disruptions, setDisruptions] = useState<AcademySession[]>([]);

  const applyDashboardPayload = useCallback((d: any) => {
    if (!d) return;

    // 1. Children and student normalization
    const rawChildren = d.children || [];
    const normalizedChildren = rawChildren.map((c: any) => {
      const p = c.profile || c.student || c;
      const normProfile: Student = {
        ...p,
        id: p.id || p.STD_IDF,
        first_name: p.first_name || p.STD_FIRST_NAME || '',
        last_name: p.last_name || p.STD_LAST_NAME || '',
        full_name: p.full_name || p.STD_FULL_NAME || `${p.STD_FIRST_NAME || ''} ${p.STD_LAST_NAME || ''}`.trim(),
        email: p.email || p.STD_EMAIL || '',
        phone: p.phone || p.STD_PHONE || '',
        category: p.category || (p.STD_CATEGORY?.toLowerCase?.() === 'junior' ? 'junior' : 'adult'),
        guardian_name: p.guardian_name || p.STD_GUARDIAN_NAME || '',
        guardian_phone: p.guardian_phone || p.STD_GUARDIAN_PHONE || '',
        guardian_email: p.guardian_email || p.STD_GUARDIAN_EMAIL || '',
        guardian_first_name: p.guardian_first_name || p.STD_GUARDIAN_FIRST_NAME || '',
        guardian_last_name: p.guardian_last_name || p.STD_GUARDIAN_LAST_NAME || '',
        guardian_relation: p.guardian_relation || p.STD_GUARDIAN_RELATION || '',
        photo_base64: formatPhotoUrl(p.photo_base64 || p.photo || p.STD_PHOTO),
        school_level: p.school_level || p.STD_SCHOOL_LEVEL || '',
        emergency_contact_name: p.emergency_contact_name || p.emergency_contact || p.STD_EMERGENCY_CONTACT || '',
        emergency_contact_phone: p.emergency_contact_phone || p.STD_EMERGENCY_PHONE || '',
        financial_hold: Boolean(p.financial_hold || p.STD_FINANCIAL_HOLD === 1 || p.STD_FINANCIAL_HOLD === true),
      };
      return {
        ...c,
        profile: normProfile,
      };
    });
    setChildrenList(normalizedChildren);

    const rawStd = d.student || (normalizedChildren.length > 0 ? (normalizedChildren[0].profile || normalizedChildren[0]) : {});
    const isParent = Boolean(d.isParent || (normalizedChildren && normalizedChildren.length > 0));

    const mappedStudent: Student = {
      ...rawStd,
      id: rawStd.id || rawStd.STD_IDF || (isParent ? 0 : 1),
      first_name: rawStd.first_name || rawStd.STD_FIRST_NAME || '',
      last_name: rawStd.last_name || rawStd.STD_LAST_NAME || '',
      full_name: rawStd.full_name || rawStd.STD_FULL_NAME || `${rawStd.STD_FIRST_NAME || ''} ${rawStd.STD_LAST_NAME || ''}`.trim() || (isParent ? 'Parent d\'élève' : 'Étudiant'),
      email: rawStd.email || rawStd.STD_EMAIL || '',
      phone: rawStd.phone || rawStd.STD_PHONE || '',
      category: isParent ? 'junior' : (rawStd.category || (rawStd.STD_CATEGORY?.toLowerCase?.() === 'junior' ? 'junior' : 'adult')),
      guardian_name: rawStd.guardian_name || rawStd.STD_GUARDIAN_NAME || '',
      guardian_phone: rawStd.guardian_phone || rawStd.STD_GUARDIAN_PHONE || '',
      guardian_email: rawStd.guardian_email || rawStd.STD_GUARDIAN_EMAIL || '',
      guardian_first_name: rawStd.guardian_first_name || rawStd.STD_GUARDIAN_FIRST_NAME || '',
      guardian_last_name: rawStd.guardian_last_name || rawStd.STD_GUARDIAN_LAST_NAME || '',
      guardian_relation: rawStd.guardian_relation || rawStd.STD_GUARDIAN_RELATION || '',
      photo_base64: formatPhotoUrl(rawStd.photo_base64 || rawStd.photo || rawStd.STD_PHOTO),
      school_level: rawStd.school_level || rawStd.STD_SCHOOL_LEVEL || '',
      emergency_contact_name: rawStd.emergency_contact_name || rawStd.emergency_contact || rawStd.STD_EMERGENCY_CONTACT || '',
      emergency_contact_phone: rawStd.emergency_contact_phone || rawStd.STD_EMERGENCY_PHONE || '',
      financial_hold: Boolean(rawStd.financial_hold || rawStd.STD_FINANCIAL_HOLD === 1 || rawStd.STD_FINANCIAL_HOLD === true),
    };
    setStudent(mappedStudent);

    // 2. Academy / Agency info
    const rawAca = d.agency || d.academy || {};
    const mappedAcademy: PortalAcademy = {
      dos_idf: rawAca.id || rawAca.dos_idf || rawAca.dosId || 1,
      dos_code: rawAca.code || rawAca.dos_code || rawAca.dosCode || '',
      dos_lib: rawAca.name || rawAca.dos_lib || rawAca.dosLib || '',
      address: rawAca.address || rawAca.dos_address || '',
      phone: rawAca.phone || rawAca.dos_tel || '',
      logo_base64: formatLogoUrl(rawAca.logo || rawAca.logo_base64 || rawAca.dosLogo || rawAca.DOS_LOGO),
    };
    setSelectedAcademy(mappedAcademy);

    // 3. Sessions normalization
    const rootSessions = d.sessions || (normalizedChildren.length > 0 ? normalizedChildren[0].sessions : []);
    const mappedSessions: AcademySession[] = (rootSessions || []).map((s: any) => {
      const startDt = s.SSN_START_HOUR ? new Date(s.SSN_START_HOUR) : null;
      const endDt = s.SSN_END_HOUR ? new Date(s.SSN_END_HOUR) : null;
      const dateStr = s.date || (startDt ? startDt.toLocaleDateString('fr-CA') : '');
      const startTimeStr = s.start_time || (startDt ? startDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
      const endTimeStr = s.end_time || (endDt ? endDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

      return {
        ...s,
        id: s.id || s.SSN_IDF,
        group_id: s.group_id || s.GRP_IDF,
        group_name: s.group_name || s.GRP_NAME,
        course_title: s.course_title || s.CRS_TITLE,
        room_name: s.room_name || s.ARM_ROOM_NUMBER,
        date: dateStr,
        start_time: startTimeStr,
        end_time: endTimeStr,
        status: s.status || s.SSN_STATUS || 'SCHEDULED',
        status_reason: s.status_reason || s.SSN_STATUS_REASON,
        postponed_date: s.postponed_date || s.SSN_POSTPONED_DATE,
        postponed_time: s.postponed_time || s.SSN_POSTPONED_TIME,
        teacher_absent: s.teacher_absent ?? s.SSN_TEACHER_ABSENT,
        attendance_taken: s.attendance_taken ?? s.SSN_ATTENDANCE_TAKEN,
      };
    });
    setSessions(mappedSessions);

    // 4. Attendances normalization
    const rootAtt = d.attendances || (normalizedChildren.length > 0 ? normalizedChildren[0].attendances : []);
    setAttendances(Array.isArray(rootAtt) ? rootAtt : []);

    // 5. Invoices & Payments
    const rootInvoices = d.invoices || (normalizedChildren.length > 0 ? normalizedChildren[0].invoices : []);
    setInvoices(Array.isArray(rootInvoices) ? rootInvoices : []);

    const rootPay = d.payments || (normalizedChildren.length > 0 ? normalizedChildren[0].payments : []);
    setPayments(Array.isArray(rootPay) ? rootPay : []);

    // 6. Disruptions & notifications
    const rootDisrupt = d.disruptions || d.notifications || (normalizedChildren.length > 0 ? (normalizedChildren[0].notifications || normalizedChildren[0].disruptions) : []);
    setDisruptions(Array.isArray(rootDisrupt) ? rootDisrupt : []);
  }, []);

  // Load Dashboard data with token
  const loadDashboardData = useCallback(async (authToken: string) => {
    if (!authToken || authToken === 'undefined' || authToken === 'null') {
      localStorage.removeItem('blobtic_portal_student_token');
      setToken(null);
      setInitialLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await academyApi.portalStudentGetData(authToken);
      if (res && res.success && res.data) {
        applyDashboardPayload(res.data);
      } else {
        localStorage.removeItem('blobtic_portal_student_token');
        setToken(null);
        setError(res?.error || res?.message || 'Session expirée. Veuillez vous reconnecter.');
      }
    } catch (err: any) {
      console.error('Error fetching student portal data:', err);
      setError('Erreur de connexion au serveur.');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [applyDashboardPayload]);

  useEffect(() => {
    if (token) {
      loadDashboardData(token);
    } else {
      setInitialLoading(false);
    }
  }, [token, loadDashboardData]);

  // Handle Login
  const handleLogin = async (login: string, pass: string) => {
    setError(null);
    setLoading(true);
    setLastLoginIdent(login);
    try {
      const res = await academyApi.portalStudentLogin(login, pass);
      if (res && res.success) {
        const authToken = res.token || res.data?.token;
        const requiresAca = res.requiresAcademySelection ?? res.data?.requiresAcademySelection ?? res.requires_academy_selection ?? res.data?.requires_academy_selection;
        const rawAcas = res.academies || res.data?.academies || [];
        const acas = rawAcas.map((a: any) => ({
          ...a,
          logo_base64: formatLogoUrl(a.logo_base64 || a.dosLogo || a.logo || a.DOS_LOGO)
        }));
        setAcademies(acas);

        if (requiresAca && acas.length > 1) {
          setShowAcademyModal(true);
        } else if (authToken) {
          localStorage.setItem('blobtic_portal_student_token', authToken);
          setToken(authToken);

          // If backend already returned full dashboard data
          const nestedData = res.data?.data;
          if (nestedData && (nestedData.student || nestedData.children || nestedData.agency)) {
            applyDashboardPayload(nestedData);
          } else {
            await loadDashboardData(authToken);
          }
        }
      } else {
        setError(res?.message || res?.error || 'Identifiant ou mot de passe incorrect.');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError('Erreur de communication avec le serveur.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Academy Selection
  const handleSelectAcademy = async (dosId: number) => {
    setLoading(true);
    try {
      const loginIdent = lastLoginIdent || token || '';
      const res = await academyApi.portalStudentSelectAcademy(loginIdent, dosId);
      if (res && res.success) {
        setShowAcademyModal(false);
        const authToken = res.token || res.data?.token;
        if (authToken) {
          localStorage.setItem('blobtic_portal_student_token', authToken);
          setToken(authToken);
          const nestedData = res.data?.data;
          if (nestedData && (nestedData.student || nestedData.children || nestedData.agency)) {
            applyDashboardPayload(nestedData);
          } else {
            await loadDashboardData(authToken);
          }
        }
      } else {
        setError(res?.message || res?.error || "Erreur lors de la sélection de l'académie.");
      }
    } catch (err) {
      console.error('Select academy error:', err);
      setError("Erreur lors de la sélection de l'académie.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.removeItem('blobtic_portal_student_token');
    setToken(null);
    setStudent(null);
    setChildrenList([]);
    setSelectedAcademy(null);
    setSessions([]);
    setAttendances([]);
    setInvoices([]);
    setPayments([]);
    setDisruptions([]);
  };

  if (initialLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress color="info" />
      </Box>
    );
  }

  // Not authenticated
  if (!token || (!student && (!childrenList || childrenList.length === 0)) || !selectedAcademy) {
    return (
      <>
        <StudentPortalLoginPage onLogin={handleLogin} loading={loading} error={error} />
        {showAcademyModal && (
          <StudentAcademySelectModal
            open={showAcademyModal}
            academies={academies}
            onSelect={handleSelectAcademy}
            loading={loading}
          />
        )}
      </>
    );
  }

  // Authenticated Dashboard
  return (
    <>
      <StudentPortalDashboard
        token={token || undefined}
        onRefresh={() => token && loadDashboardData(token)}
        student={student}
        childrenList={childrenList}
        academy={selectedAcademy}
        academiesCount={academies.length}
        sessions={sessions}
        attendances={attendances}
        invoices={invoices}
        payments={payments}
        disruptions={disruptions}
        onSwitchAcademy={() => setShowAcademyModal(true)}
        onLogout={handleLogout}
      />
      {showAcademyModal && (
        <StudentAcademySelectModal
          open={showAcademyModal}
          academies={academies}
          onSelect={handleSelectAcademy}
          loading={loading}
        />
      )}
    </>
  );
};

export const StudentPortalApp: React.FC = () => {
  return (
    <StudentPortalLanguageProvider>
      <StudentPortalInner />
    </StudentPortalLanguageProvider>
  );
};

export default StudentPortalApp;
